import React from 'react';
import {
	Box,
	Typography,
	Grid,
	Accordion,
	AccordionSummary,
	AccordionDetails,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import Input from '../../../common/form/Input';
import MaskInput from '../../../common/form/MaskInput';
import Select from '../../../common/form/Select';
import RadioButtons from '../../../common/buttons/RadioButtons';
import Checkbox from '../../../common/form/Checkbox';
import DatePicker from '../../../common/form/DatePicker';
import { FormValues } from './types';
import { stateOptions } from './constants';
import ProfileAvatar from '../../../common/ProfileAvatar';

interface BasicInformationStepProps {
	formValues: FormValues;
	setValue: any;
	control: any;
	errors: any;
	isRequired?: boolean;
	token?: string;
	onDrop?: (acceptedFiles: File[]) => void;
}

export default function BasicInformationStep({
	formValues,
	setValue,
	control,
	errors,
	isRequired = true,
	token,
	onDrop,
}: BasicInformationStepProps) {
	return (
		<Box sx={{ py: 2 }}>
			{/* Name */}
			<Typography variant="h6" sx={{ mb: 2, color: 'text.label' }}>
				Name
			</Typography>

			<ProfileAvatar
				profilePictureUrl={formValues.profilePictureUrl}
				firstName={formValues.firstName}
				lastName={formValues.lastName}
				onDrop={onDrop}
			/>

			<Grid container spacing={3} sx={{ mb: 4 }}>
				<Grid item xs={12} sm={6}>
					<Controller
						name="firstName"
						control={control}
						rules={
							isRequired
								? { required: 'First name is required' }
								: {}
						}
						render={({ field }) => (
							<Input
								{...field}
								label="Legal first name"
								required={isRequired}
								fullWidth
								error={!!errors.firstName}
								helperText={errors.firstName?.message}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<Controller
						name="lastName"
						control={control}
						rules={
							isRequired
								? { required: 'Last name is required' }
								: {}
						}
						render={({ field }) => (
							<Input
								{...field}
								label="Legal last name"
								required={isRequired}
								fullWidth
								error={!!errors.lastName}
								helperText={errors.lastName?.message}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<Controller
						name="preferredName"
						control={control}
						rules={
							isRequired
								? { required: 'Preferred name is required' }
								: {}
						}
						render={({ field }) => (
							<Input
								{...field}
								label="Preferred name (nickname)"
								required={isRequired}
								fullWidth
								error={!!errors.preferredName}
								helperText={errors.preferredName?.message}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<Controller
						name="preferredPronouns"
						control={control}
						render={({ field }) => (
							<Select
								{...field}
								label="Preferred Pronouns"
								options={[
									{ id: 'he/him', display: 'He/Him' },
									{ id: 'she/her', display: 'She/Her' },
									{
										id: 'they/them',
										display: 'They/Them',
									},
									{ id: 'other', display: 'Other' },
								]}
								fullWidth
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<Controller
						name="dateOfBirth"
						control={control}
						rules={
							isRequired
								? { required: 'Date of birth is required' }
								: {}
						}
						render={({ field }) => (
							<DatePicker
								{...field}
								label="Date of birth"
								placeholder="MM/DD/YYYY"
								required={isRequired}
								fullWidth
								error={!!errors.dateOfBirth}
								helperText={errors.dateOfBirth?.message}
							/>
						)}
					/>
				</Grid>
			</Grid>

			{/* Contact Information */}
			<Typography variant="h6" sx={{ mb: 2, color: 'text.label' }}>
				Contact Information
			</Typography>

			<Grid container spacing={3} sx={{ mb: 4 }}>
				<Grid item xs={12}>
					<Controller
						name="email"
						control={control}
						rules={
							isRequired
								? {
										required: 'Email address is required',
										pattern: {
											value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
											message:
												'Please enter a valid email address',
										},
								  }
								: {
										pattern: {
											value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
											message:
												'Please enter a valid email address',
										},
								  }
						}
						render={({ field }) => (
							<Input
								{...field}
								label="Email address"
								type="email"
								required={isRequired}
								fullWidth
								error={!!errors.email}
								helperText={errors.email?.message}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={4}>
					<Controller
						name="phone"
						control={control}
						rules={
							isRequired
								? { required: 'Cell phone # is required' }
								: {}
						}
						render={({ field }) => (
							<MaskInput
								{...field}
								id="phone"
								name="phone"
								required={isRequired}
								label="Cell phone #"
								mask="phone"
								fullWidth
								error={!!errors.phone}
								helperText={errors.phone?.message}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={8}>
					<Controller
						name="phoneCarrier"
						control={control}
						rules={
							isRequired
								? { required: 'Phone carrier is required' }
								: {}
						}
						render={({ field }) => (
							<Select
								{...field}
								label="Phone carrier"
								options={[
									{ id: 'verizon', display: 'Verizon' },
									{ id: 'att', display: 'AT&T' },
									{ id: 'tmobile', display: 'T-Mobile' },
									{ id: 'sprint', display: 'Sprint' },
									{
										id: 'us-cellular',
										display: 'US Cellular',
									},
									{
										id: 'straight-talk',
										display: 'Straight Talk',
									},
									{ id: 'tracfone', display: 'TracFone' },
									{ id: 'visible', display: 'Visible' },
									{
										id: 'mint-mobile',
										display: 'Mint Mobile',
									},
									{
										id: 'total-wireless',
										display: 'Total Wireless',
									},
									{
										id: 'boost-mobile',
										display: 'Boost Mobile',
									},
									{ id: 'metro-pcs', display: 'MetroPCS' },
									{
										id: 'cricket-wireless',
										display: 'Cricket Wireless',
									},
									{ id: 'freedompop', display: 'FreedomPop' },
									{ id: 'text-now', display: 'TextNow' },
									{ id: 'other', display: 'Other' },
								]}
								required={isRequired}
								fullWidth
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12}>
					<Controller
						name="isIphone"
						control={control}
						render={({ field }) => (
							<RadioButtons
								label="Is this an iPhone?"
								name="isIphone"
								options={[
									{ value: 'yes', label: 'Yes' },
									{ value: 'no', label: 'No' },
								]}
								value={field.value}
								onChange={field.onChange}
								row
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12}>
					<Controller
						name="smsTextReady"
						control={control}
						render={({ field }) => (
							<RadioButtons
								label="Is this # sms text ready?"
								name="smsTextReady"
								options={[
									{ value: 'yes', label: 'Yes' },
									{ value: 'no', label: 'No' },
								]}
								value={field.value}
								onChange={field.onChange}
								row
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12}>
					<Controller
						name="hasWhatsapp"
						control={control}
						render={({ field }) => (
							<RadioButtons
								label="Do you have a WhatsApp account?"
								name="hasWhatsapp"
								options={[
									{ value: 'yes', label: 'Yes' },
									{ value: 'no', label: 'No' },
								]}
								value={field.value}
								onChange={field.onChange}
								row
							/>
						)}
					/>
				</Grid>
				{formValues.hasWhatsapp === 'yes' && (
					<Grid item xs={12}>
						<Controller
							name="whatsappNumber"
							control={control}
							rules={
								isRequired && formValues.hasWhatsapp === 'yes'
									? {
											required:
												'WhatsApp number is required',
									  }
									: {}
							}
							render={({ field }) => (
								<MaskInput
									{...field}
									id="whatsappNumber"
									name="whatsappNumber"
									required={isRequired}
									label="Enter connected WhatsApp number"
									mask="phone"
									fullWidth
									error={!!errors.whatsappNumber}
									helperText={errors.whatsappNumber?.message}
								/>
							)}
						/>
					</Grid>
				)}
				<Grid item xs={12}>
					<Controller
						name="hasWechat"
						control={control}
						render={({ field }) => (
							<RadioButtons
								label="Do you have a WeChat account?"
								name="hasWechat"
								options={[
									{ value: 'yes', label: 'Yes' },
									{ value: 'no', label: 'No' },
								]}
								value={field.value}
								onChange={field.onChange}
								row
							/>
						)}
					/>
				</Grid>
				{formValues.hasWechat === 'yes' && (
					<Grid item xs={12}>
						<Controller
							name="wechatNumber"
							control={control}
							rules={
								isRequired && formValues.hasWechat === 'yes'
									? {
											required:
												'WeChat number is required',
									  }
									: {}
							}
							render={({ field }) => (
								<MaskInput
									{...field}
									id="wechatNumber"
									name="wechatNumber"
									required={isRequired}
									label="Enter connected WeChat number"
									mask="phone"
									fullWidth
									error={!!errors.wechatNumber}
									helperText={errors.wechatNumber?.message}
								/>
							)}
						/>
					</Grid>
				)}
			</Grid>

			{/* Home Address */}
			<Accordion
				defaultExpanded
				sx={{
					mb: 4,
					backgroundColor: '#2F2F2F',
					boxShadow: 'none',
					borderRadius: '16px',
					'&:before': {
						display: 'none',
					},
				}}
			>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					sx={{
						backgroundColor: '#2F2F2F',
						'& .MuiAccordionSummary-content': {
							margin: '12px 0',
						},
						borderTopLeftRadius: '8px',
						borderTopRightRadius: '8px',
					}}
				>
					<Typography variant="h6" sx={{ color: 'text.label' }}>
						Home Address
					</Typography>
				</AccordionSummary>
				<AccordionDetails
					sx={{
						backgroundColor: '#2F2F2F',
						borderBottomLeftRadius: '8px',
						borderBottomRightRadius: '8px',
					}}
				>
					<Grid container spacing={3}>
						<Grid item xs={12}>
							<Controller
								name="homeAddress.line1"
								control={control}
								rules={
									isRequired
										? {
												required:
													'Street address line 1 is required',
										  }
										: {}
								}
								render={({ field }) => (
									<Input
										{...field}
										label="Street address line 1"
										required={isRequired}
										fullWidth
										error={!!errors.homeAddress?.line1}
										helperText={
											errors.homeAddress?.line1?.message
										}
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12}>
							<Controller
								name="homeAddress.line2"
								control={control}
								render={({ field }) => (
									<Input
										{...field}
										label="Street address line 2"
										fullWidth
										error={!!errors.homeAddress?.line2}
										helperText={
											errors.homeAddress?.line2?.message
										}
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} sm={4}>
							<Controller
								name="homeAddress.city"
								control={control}
								rules={
									isRequired
										? { required: 'City is required' }
										: {}
								}
								render={({ field }) => (
									<Input
										{...field}
										label="City"
										required={isRequired}
										fullWidth
										error={!!errors.homeAddress?.city}
										helperText={
											errors.homeAddress?.city?.message
										}
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} sm={4}>
							<Controller
								name="homeAddress.state"
								control={control}
								rules={
									isRequired
										? { required: 'State is required' }
										: {}
								}
								render={({ field }) => (
									<Select
										{...field}
										label="State"
										options={stateOptions}
										required={isRequired}
										fullWidth
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} sm={4}>
							<Controller
								name="homeAddress.zip"
								control={control}
								rules={
									isRequired
										? { required: 'Zip code is required' }
										: {}
								}
								render={({ field }) => (
									<Input
										{...field}
										label="Zip code"
										required={isRequired}
										fullWidth
										error={!!errors.homeAddress?.zip}
										helperText={
											errors.homeAddress?.zip?.message
										}
									/>
								)}
							/>
						</Grid>
					</Grid>
				</AccordionDetails>
			</Accordion>

			{/* Shipping Address */}
			<Accordion
				defaultExpanded
				sx={{
					mb: 4,
					backgroundColor: '#2F2F2F',
					boxShadow: 'none',
					borderRadius: '16px',
					borderBottomLeftRadius: '8px !important',
					borderBottomRightRadius: '8px !important',
					'&:before': {
						display: 'none',
					},
				}}
			>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					sx={{
						backgroundColor: '#2F2F2F',
						'& .MuiAccordionSummary-content': {
							margin: '12px 0',
						},
						borderTopLeftRadius: '8px',
						borderTopRightRadius: '8px',
					}}
				>
					<Typography variant="h6" sx={{ color: 'text.label' }}>
						Shipping Address
					</Typography>
				</AccordionSummary>
				<AccordionDetails
					sx={{
						backgroundColor: '#2F2F2F',
						borderBottomLeftRadius: '8px',
						borderBottomRightRadius: '8px',
					}}
				>
					<Box sx={{ mb: 2 }}>
						<Controller
							name="shippingIsSameAsHome"
							control={control}
							render={({ field }) => (
								<Checkbox
									items={[
										{
											id: 'shippingIsSameAsHome',
											name: 'Same as Home Address',
											checked: field.value,
											handleOnChange: field.onChange,
										},
									]}
								/>
							)}
						/>
					</Box>

					{!formValues.shippingIsSameAsHome && (
						<Grid container spacing={3}>
							<Grid item xs={12}>
								<Controller
									name="shippingAddress.line1"
									control={control}
									render={({ field }) => (
										<Input
											{...field}
											label="Street address line 1"
											fullWidth
											error={
												!!errors.shippingAddress?.line1
											}
											helperText={
												errors.shippingAddress?.line1
													?.message
											}
										/>
									)}
								/>
							</Grid>
							<Grid item xs={12}>
								<Controller
									name="shippingAddress.line2"
									control={control}
									render={({ field }) => (
										<Input
											{...field}
											label="Street address line 2"
											fullWidth
											error={
												!!errors.shippingAddress?.line2
											}
											helperText={
												errors.shippingAddress?.line2
													?.message
											}
										/>
									)}
								/>
							</Grid>
							<Grid item xs={12} sm={4}>
								<Controller
									name="shippingAddress.city"
									control={control}
									render={({ field }) => (
										<Input
											{...field}
											label="City"
											fullWidth
											error={
												!!errors.shippingAddress?.city
											}
											helperText={
												errors.shippingAddress?.city
													?.message
											}
										/>
									)}
								/>
							</Grid>
							<Grid item xs={12} sm={4}>
								<Controller
									name="shippingAddress.state"
									control={control}
									render={({ field }) => (
										<Select
											{...field}
											label="State"
											options={stateOptions}
											fullWidth
										/>
									)}
								/>
							</Grid>
							<Grid item xs={12} sm={4}>
								<Controller
									name="shippingAddress.zip"
									control={control}
									render={({ field }) => (
										<Input
											{...field}
											label="Zip code"
											fullWidth
											error={
												!!errors.shippingAddress?.zip
											}
											helperText={
												errors.shippingAddress?.zip
													?.message
											}
										/>
									)}
								/>
							</Grid>
						</Grid>
					)}
				</AccordionDetails>
			</Accordion>

			{/* Emergency Contact */}
			<Typography variant="h6" sx={{ mb: 2, color: 'text.label' }}>
				Emergency Contact
			</Typography>

			<Grid container spacing={3} sx={{ mb: 4 }}>
				<Grid item xs={12} sm={4}>
					<Controller
						name="emergencyContactFirstName"
						control={control}
						rules={
							isRequired
								? {
										required:
											'Emergency contact first name is required',
								  }
								: {}
						}
						render={({ field }) => (
							<Input
								{...field}
								label="First name"
								required={isRequired}
								fullWidth
								error={!!errors.emergencyContactFirstName}
								helperText={
									errors.emergencyContactFirstName?.message
								}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={4}>
					<Controller
						name="emergencyContactLastName"
						control={control}
						rules={
							isRequired
								? {
										required:
											'Emergency contact last name is required',
								  }
								: {}
						}
						render={({ field }) => (
							<Input
								{...field}
								label="Last name"
								required={isRequired}
								fullWidth
								error={!!errors.emergencyContactLastName}
								helperText={
									errors.emergencyContactLastName?.message
								}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={4}>
					<Controller
						name="emergencyContactPhone"
						control={control}
						rules={
							isRequired
								? {
										required:
											'Emergency contact phone number is required',
								  }
								: {}
						}
						render={({ field }) => (
							<MaskInput
								{...field}
								id="emergencyContactPhone"
								name="emergencyContactPhone"
								required={isRequired}
								label="Phone number"
								mask="phone"
								fullWidth
								error={!!errors.emergencyContactPhone}
								helperText={
									errors.emergencyContactPhone?.message
								}
							/>
						)}
					/>
				</Grid>
			</Grid>
		</Box>
	);
}
